import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import {
    Modal,
    Button,
    TextControl,
    SelectControl,
} from '@wordpress/components';

const PEXELS_API = 'JXdfZlHLSUKs2VKIARkcgBYm3OcJEiFO6pUQMjaKAHd3wUygetgEWahT';

const PexelsSearchModal = ( { isOpen, onClose, onSelect } ) => {
    const [ query, setQuery ] = useState( '' );
    const [ color, setColor ] = useState( '' );
    const [ orientation, setOrientation ] = useState( '' );
    const [ images, setImages ] = useState( [] );
    const [ loading, setLoading ] = useState( false );
    const [ page, setPage ] = useState( 1 );
    const [ totalPages, setTotalPages ] = useState( 1 );

    const searchImages = ( searchPage = 1, append = false ) => {
        setLoading( true );
        let url = `https://api.pexels.com/v1/search?query=${ encodeURIComponent(
            query
        ) }&per_page=12&page=${ searchPage }`;
        if ( color ) url += `&color=${ encodeURIComponent( color ) }`;
        if ( orientation )
            url += `&orientation=${ encodeURIComponent( orientation ) }`;

        fetch( url, {
            headers: { Authorization: PEXELS_API },
        } )
            .then( ( res ) => res.json() )
            .then( ( data ) => {
                if ( data && data.photos ) {
                    setImages( append ? [ ...images, ...data.photos ] : data.photos );
                    setTotalPages(
                        data.total_results
                            ? Math.ceil( data.total_results / 12 )
                            : 1
                    );
                }
            } )
            .finally( () => setLoading( false ) );
    };

    const loadCurated = ( curatedPage = 1, append = false ) => {
        setLoading( true );
        let url = `https://api.pexels.com/v1/curated?per_page=12&page=${ curatedPage }`;
        fetch( url, {
            headers: { Authorization: PEXELS_API },
        } )
            .then( ( res ) => res.json() )
            .then( ( data ) => {
                if ( data && data.photos ) {
                    setImages( append ? [ ...images, ...data.photos ] : data.photos );
                    setTotalPages(
                        data.total_results
                            ? Math.ceil( data.total_results / 12 )
                            : 1
                    );
                }
            } )
            .finally( () => setLoading( false ) );
    };

    useEffect( () => {
        if ( isOpen ) {
            loadCurated();
        }
    }, [ isOpen ] );

    const handleSearch = ( e ) => {
        e.preventDefault();
        setPage( 1 );
        if ( query.trim() ) {
            searchImages( 1 );
        } else {
            loadCurated( 1 );
        }
    };

    const handlePageChange = ( newPage ) => {
        if ( newPage > 0 && newPage <= totalPages ) {
            setPage( newPage );
            if ( query.trim() ) {
                searchImages( newPage, false );
            } else {
                loadCurated( newPage, false );
            }
        }
    };

    if ( ! isOpen ) return null;

    const Pagination = ( { currentPage, total, onPageChange } ) => {
        if ( total <= 1 ) return null;

        const getPages = () => {
            const delta = 2;
            const range = [];
            for (let i = Math.max(2, currentPage - delta); i <= Math.min(total - 1, currentPage + delta); i++) {
                range.push(i);
            }

            if (currentPage - delta > 2) {
                range.unshift("...");
            }
            if (currentPage + delta < total - 1) {
                range.push("...");
            }

            range.unshift(1);
            if (total > 1) {
                range.push(total);
            }

            // remove duplicates
            return [...new Set(range)];
        }
        const pages = getPages();

        return (
            <div className="pexels-pagination">
                <Button
                    isPrimary={ currentPage > 1 }
                    disabled={ currentPage === 1 }
                    onClick={ () => onPageChange( currentPage - 1 ) }
                >
                    { __( 'Previous', 'alpha' ) }
                </Button>
                { pages.map( ( p, index ) => (
                    <Button
                        key={ index }
                        isPrimary={ currentPage === p }
                        disabled={ p === "..." }
                        onClick={ () => onPageChange( p ) }
                    >
                        { p }
                    </Button>
                ) ) }
                <Button
                    isPrimary={ currentPage < total }
                    disabled={ currentPage === total }
                    onClick={ () => onPageChange( currentPage + 1 ) }
                >
                    { __( 'Next', 'alpha' ) }
                </Button>
            </div>
        );
    };

    const colorOptions = [
        { label: 'Any Color', value: '' },
        { label: 'Red', value: 'red' },
        { label: 'Orange', value: 'orange' },
        { label: 'Yellow', value: 'yellow' },
        { label: 'Green', value: 'green' },
        { label: 'Turquoise', value: 'turquoise' },
        { label: 'Blue', value: 'blue' },
        { label: 'Violet', value: 'violet' },
        { label: 'Pink', value: 'pink' },
        { label: 'Brown', value: 'brown' },
        { label: 'Black', value: 'black' },
        { label: 'Gray', value: 'gray' },
        { label: 'White', value: 'white' },
    ];

    const orientationOptions = [
        { label: 'Any Orientation', value: '' },
        { label: 'Landscape', value: 'landscape' },
        { label: 'Portrait', value: 'portrait' },
        { label: 'Square', value: 'square' },
    ];

    return (
        <Modal
            title={ __( 'Search Pexels for Images', 'alpha' ) }
            onRequestClose={ onClose }
            className="alpha-pexels-search-modal"
        >
            <form onSubmit={ handleSearch } className="pexels-search-form">
                <TextControl
                    value={ query }
                    onChange={ setQuery }
                    placeholder={ __( 'Search for images...', 'alpha' ) }
                />
                <SelectControl
                    value={ color }
                    options={ colorOptions }
                    onChange={ setColor }
                />
                <SelectControl
                    value={ orientation }
                    options={ orientationOptions }
                    onChange={ setOrientation }
                />
                <Button isPrimary onClick={ handleSearch } disabled={ loading }>
                    { __( 'Search', 'alpha' ) }
                </Button>
            </form>
            <div className="pexels-results-grid">
                { loading && (
                    <div className="pexels-loader-overlay">
                        <div className="pexels-loader-spinner"></div>
                    </div>
                ) }
                { images.map( ( img ) => (
                    <div
                        key={ img.id }
                        className="pexels-image-item"
                        onClick={ () => onSelect( img.src.original ) }
                    >
                        <img src={ img.src.medium } alt={ img.alt } />
                    </div>
                ) ) }
            </div>
            { ! loading && totalPages > 1 && (
                <Pagination
                    currentPage={ page }
                    total={ totalPages }
                    onPageChange={ handlePageChange }
                />
            ) }
            <style>{ `
                .alpha-pexels-search-modal .components-modal__content {
                    width: 60vw;
                }
                .pexels-search-form {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }
                .pexels-results-grid {
                    position: relative;
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 1rem;
                    min-height: 50vh;
                }
                .pexels-loader-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(255, 255, 255, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10;
                }
                .pexels-loader-spinner {
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #3498db;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .pexels-image-item {
                    cursor: pointer;
                    border: 2px solid transparent;
                    transition: border-color 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f0f0f0;
                }
                .pexels-image-item:hover {
                    border-color: #007cba;
                }
                .pexels-image-item img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }
                .pexels-pagination {
                    display: flex;
                    justify-content: center;
                    gap: 0.5rem;
                    margin-top: 1rem;
                }
            ` }</style>
        </Modal>
    );
};

export default PexelsSearchModal;
